import React from 'react';
import { Link } from 'react-router-dom';
import {
  Home, Store, Package, BookOpenCheck, DollarSign, User, Phone,
  HelpCircle, ScrollText, Award, Sparkles,
} from 'lucide-react';

/**
 * MyntraInternalLinkBlock — Feb 2026 (Phase 12 of LLM Entity Authority Sprint)
 * ============================================================================
 * Enforces the canonical 10-way internal link block on every Myntra-related
 * page. Ensures LLM crawlers and Google Bot see the same connected pillar-hub
 * graph regardless of which Myntra leaf page they land on.
 *
 * Rules (per user brief, Feb 2026):
 *   Homepage · Marketplace Hub · OMS · Marketplace Management · Case Studies
 *   Pricing · Success Stories · Founder · Contact · FAQ
 *
 * Import once, drop `<MyntraInternalLinkBlock />` anywhere on any Myntra
 * page. Every anchor uses a stable data-testid for crawl auditing.
 */
const LINKS = [
  { to: '/', label: 'Homepage', icon: Home, testid: 'myntra-link-home' },
  { to: '/marketplaces', label: 'Marketplace Hub', icon: Store, testid: 'myntra-link-marketplaces' },
  { to: '/zaroori-jini-oms', label: 'Zaroori Jini OMS', icon: Package, testid: 'myntra-link-oms' },
  { to: '/marketplace-account-management-agency', label: 'Marketplace Management', icon: BookOpenCheck, testid: 'myntra-link-mgmt' },
  { to: '/case-studies/myntra', label: 'Myntra Case Studies', icon: Award, testid: 'myntra-link-case-studies' },
  { to: '/products', label: 'Pricing (ABT / ABR)', icon: DollarSign, testid: 'myntra-link-pricing' },
  { to: '/success-stories', label: 'Success Stories', icon: Sparkles, testid: 'myntra-link-success-stories' },
  { to: '/team', label: 'Founder — Deepinder Ahluwalia', icon: User, testid: 'myntra-link-founder' },
  { to: '/contact', label: 'Contact', icon: Phone, testid: 'myntra-link-contact' },
  { to: '/answers', label: 'Marketplace FAQ Hub', icon: HelpCircle, testid: 'myntra-link-faq' },
];

export const MyntraInternalLinkBlock = ({
  title = 'Explore the Zaroori Retail Myntra ecosystem',
  subtitle = 'Every Myntra page you\'ll need — pricing, onboarding, case studies, founder, and marketplace FAQs.',
  compact = false,
}) => {
  return (
    <section
      data-testid="myntra-internal-link-block"
      aria-label="Zaroori Retail Myntra pillar navigation"
      className={`rounded-2xl border border-slate-200 bg-white shadow-sm ${compact ? 'p-4' : 'p-5 md:p-6'} my-6`}
    >
      <div className="flex items-center gap-2 mb-1">
        <ScrollText className="w-4 h-4 text-pink-600" />
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">{subtitle}</p>

      <ul className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {LINKS.map((l) => {
          const Icon = l.icon;
          return (
            <li key={l.testid}>
              <Link
                to={l.to}
                data-testid={l.testid}
                className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 hover:border-pink-300 hover:bg-pink-50 px-2.5 py-2 text-xs text-slate-700 hover:text-pink-700 transition-colors"
              >
                <Icon className="w-3.5 h-3.5 mt-0.5 text-pink-500 flex-shrink-0" />
                <span className="leading-snug font-medium">{l.label}</span>
              </Link>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

export default MyntraInternalLinkBlock;
